import debounce from 'lodash/debounce'
import {
  Agent,
  AgentSearchResponse,
  AgentSearchCriteria,
  Location
} from '@/types/agents'
import {
  Action,
  Thunk,
  action,
  thunk,
  computed,
  Computed,
  thunkOn,
  ThunkOn
} from 'easy-peasy'
import { agentApi } from '@/services/agent-api'
import rollbar from '@/services/rollbar'
import {
  populateLocationInUrl,
  updateHeadings,
  updateLocationObject,
  updateTitleTag
} from '@/utils/seo'
import bamEvents from '@/services/bam-events'
import {
  LocationsNearbyResponse,
  LocationsNearbyQuery
} from '@/types/locations'
import { v4 as uuidv4 } from 'uuid'

export const agentsOnPageLoad = 15
export const agentsPerPage = 8

type Validator = { validator: (value: any) => boolean; message: string }

export interface SearchModel {
  totalResults: number // the total number of agents
  displayCount: number // the number of results shown
  isSearching: 0 | 1 | 2 // becomes truthy when the search starts and false when it ends
  queryResponse: AgentSearchResponse // stores raw API response
  locationsNearbyResponse: LocationsNearbyResponse
  h1Tag: string
  embedded: boolean
  results: Computed<SearchModel, Agent[]> // a collection of the visible agents
  primaryLocation: Computed<SearchModel, Location>
  contentModifiedAt: Computed<SearchModel, Date>
  listId: Computed<SearchModel, string>

  setInitialSSRData: Action<
    SearchModel,
    {
      dimensions: Partial<AgentSearchCriteria>
      queryResponse: AgentSearchResponse
      locationsNearbyResponse: LocationsNearbyResponse
      embedded: boolean
    }
  >
  setDisplayCount: Action<SearchModel, number> // updates the display count
  setQueryResponse: Action<SearchModel, AgentSearchResponse> // updates the raw query response
  setIsSearching: Action<SearchModel, 0 | 1 | 2> // 0: no, 1: yes but dont show shimmer, 2: yes and show shimmer
  setLocationsNearbyResponse: Action<SearchModel, LocationsNearbyResponse>
  setH1Tag: Action<SearchModel, string>

  triggerSearch: Thunk<SearchModel, null>
  fetchNearbyLocations: Thunk<SearchModel, null>

  dimensions: AgentSearchCriteria // store user inputs
  setDimensions: Action<SearchModel, Partial<AgentSearchCriteria>> // update user inputs, and trigger a search
  setZipcode: Action<SearchModel, AgentSearchCriteria['zipcode']> // same as setDimensions but doesn't trigger a search
  setLimit: Action<SearchModel, AgentSearchCriteria['limit']> // same as setDimensions but doesn't trigger a search
  onDimensionsChange: ThunkOn<SearchModel, unknown, SearchModel>
  dimensionsInvalid: Computed<SearchModel, boolean> // becomes truthy when the user inputs are invalid
  validators: Partial<Record<keyof AgentSearchCriteria, Validator[]>>
  postResizeEvent: Action<SearchModel>
}

const debouncedSearch = debounce((searchFn: () => void) => searchFn(), 1000)
const debouncedBamEvents = debounce(
  (bamEventFn: () => void) => bamEventFn(),
  100
)

export const initialqueryResponse: AgentSearchResponse = {
  limit: 0,
  total: 0,
  location_data: {
    locations: [],
    primary_location: { state: '', city: '', zipcode: '' },
    anchors: []
  },
  results: [],
  sorty_by: 'experience'
}

export const baseDimensions: AgentSearchCriteria = {
  zipcode: null,
  city: null,
  state: null,
  buy_sell: 'buy-sell',
  price_from: 0,
  price_to: 99999999,
  property_type: 'single_family,condominium,townhome',
  sort_by: 'transactions',
  limit: agentsOnPageLoad
}

const search: SearchModel = {
  totalResults: 0,
  locationsNearbyResponse: null,
  queryResponse: JSON.parse(JSON.stringify(initialqueryResponse)),
  displayCount: agentsOnPageLoad,
  isSearching: 0,
  embedded: false,
  h1Tag: '',

  results: computed(state => {
    if (state.dimensions.limit && state.dimensions.limit > 0) {
      state.displayCount = state.dimensions.limit
    } else {
      state.displayCount = agentsOnPageLoad
    }
    return state.queryResponse?.results.slice(0, state.displayCount)
  }),

  primaryLocation: computed(state => {
    return state.queryResponse?.location_data.primary_location
  }),

  contentModifiedAt: computed(state =>
    state.queryResponse?.content_modified_at
      ? new Date(state.queryResponse.content_modified_at)
      : null
  ),

  listId: computed(state => {
    return state.queryResponse?.request_id || uuidv4()
  }),

  setInitialSSRData: action((state, payload) => {
    state.totalResults = payload.queryResponse.total

    state.locationsNearbyResponse = payload.locationsNearbyResponse

    state.dimensions = {
      ...state.dimensions,
      ...payload?.dimensions
    }

    state.embedded = payload.embedded

    state.queryResponse = payload.queryResponse
  }),

  setH1Tag: action((state, h1Tag) => {
    state.h1Tag = h1Tag
  }),

  setDisplayCount: action((state, count) => {
    state.displayCount = count
  }),

  setQueryResponse: action((state, response) => {
    state.queryResponse = response
    state.totalResults = response.total
  }),

  setIsSearching: action((state, bool) => {
    state.isSearching = bool
  }),

  triggerSearch: thunk(async (actions, _ = null, { getState }) => {
    if (!getState().isSearching) {
      actions.setIsSearching(2)
    }

    const res = await agentApi.fetchAgents(getState().dimensions)

    let nearbyResponse = await agentApi.fetchNearbyLocations(
      getState().dimensions.zipcode
    )

    if (!nearbyResponse?.nearby_locations?.length) {
      const baseLocationsClosestDimensions: LocationsNearbyQuery = {
        limit: 4,
        radius: 3000,
        sort_by: 'distance_in_miles'
      }

      nearbyResponse = await agentApi.fetchNearbyLocations(
        getState().dimensions.zipcode,
        baseLocationsClosestDimensions
      )
    }

    // save api response in store
    actions.setQueryResponse(res)

    // save nearby locations in store
    actions.setLocationsNearbyResponse(nearbyResponse)

    try {
      // SEO
      const { city = '', state = '' } = res.location_data.primary_location
      updateLocationObject(city, state)
      updateTitleTag(city, state)
      actions.setH1Tag(updateHeadings(city, state))
      if (res.results.length >= 10) {
        populateLocationInUrl(city, state)
      }
    } catch (err) {
      rollbar.error('failed to update seo tags with api data', err)
    }

    debouncedBamEvents(actions.postResizeEvent)

    actions.setIsSearching(0)
  }),

  fetchNearbyLocations: thunk(async (actions, _ = null, { getState }) => {
    const res = await agentApi.fetchNearbyLocations(
      getState().queryResponse.location_data.primary_location.zipcode
    )
    actions.setLocationsNearbyResponse(res)
  }),

  setLocationsNearbyResponse: action((state, locations) => {
    state.locationsNearbyResponse = locations
  }),

  dimensions: { ...baseDimensions },

  validators: {
    zipcode: [
      {
        validator: value => value === null || /^\d{5}$/.test(value),
        message: 'Please enter a valid ZIP code'
      }
    ]
  },

  dimensionsInvalid: computed(state => {
    for (const fieldName in state.dimensions) {
      const validators: Validator[] = state.validators[fieldName] || []
      const fieldValue = state.dimensions[fieldName]
      for (const one of validators) {
        if (!one.validator(fieldValue)) return true
      }
    }
    return false // meaning no errors
  }),

  setDimensions: action((state, payload) => {
    state.dimensions = {
      ...state.dimensions,
      ...payload
    }
  }),

  setZipcode: action((state, payload) => {
    state.dimensions = {
      ...state.dimensions,
      zipcode: payload
    }
  }),

  setLimit: action((state, payload) => {
    state.dimensions = {
      ...state.dimensions,
      limit: payload
    }
  }),

  onDimensionsChange: thunkOn(
    actions => actions.setDimensions,
    (actions, _, { getState }) => {
      if (!getState().dimensionsInvalid) {
        debouncedSearch(actions.triggerSearch)
      }
    }
  ),

  postResizeEvent: action(() => {
    bamEvents.onResultsChanged()
  })
}

export default search
